import { union } from "lodash"
import { useCallback, useEffect, useRef, useState } from "react"
import { type Socket } from "socket.io-client"
import { getIntradaySocket, getOrCreateIntradaySocket } from "socket/intraday-connection"
import {
  subscribeToPrices,
  addPriceSubscription,
  removePriceSubscription,
  unsubscribeFromPrices,
  PRICES_UPDATED_EVENT,
  type PricesUpdatedPayload,
} from "socket/intraday-prices"
import { computeListDiff, useIntradaySocketConnection } from "./use-intraday-socket-connection"

export interface UseIntradayPriceStreamParams {
  modelProviderIds: string[]
  modelId: string
  enabled: boolean
}

export interface UseIntradayPriceStreamResult {
  prices: PricesUpdatedPayload
  lastUpdatedAt: number | null
  updatedModelProviderIds: string[]
}

export function useIntradayPriceStream({
  modelProviderIds,
  modelId,
  enabled,
}: UseIntradayPriceStreamParams): UseIntradayPriceStreamResult {
  const [prices, setPrices] = useState<PricesUpdatedPayload>({})
  const [lastUpdatedAt, setLastUpdatedAt] = useState<number | null>(null)
  const [updatedModelProviderIds, setUpdatedModelProviderIds] = useState<string[]>([])
  const subscribedIdsRef = useRef<string[]>([])
  const previousModelIdRef = useRef<string>(modelId)

  const handleUnmount = useCallback((socket: Socket | undefined) => {
    if (socket) unsubscribeFromPrices(socket)
  }, [])

  const { accountId, registerSocketConsumer } = useIntradaySocketConnection(handleUnmount)

  useEffect(() => {
    if (!accountId) {
      return
    }

    if (!enabled || modelProviderIds.length === 0) {
      const socket = getIntradaySocket(accountId)
      if (socket && subscribedIdsRef.current.length > 0) {
        unsubscribeFromPrices(socket)
        subscribedIdsRef.current = []
      }
      return
    }

    registerSocketConsumer()
    const socket = getOrCreateIntradaySocket(accountId)

    const reconcilePriceSubscriptions = (forceFullResync: boolean): void => {
      const modelIdChanged = previousModelIdRef.current !== modelId
      previousModelIdRef.current = modelId

      // Flush the accumulated price cache when switching models or doing a full resync,
      // so stale prices from the previous model don't persist in the UI.
      if (forceFullResync || modelIdChanged) {
        setPrices({})
        setLastUpdatedAt(null)
        setUpdatedModelProviderIds([])
      }

      const prev = subscribedIdsRef.current
      const { added, removed } = computeListDiff(prev, modelProviderIds)

      if (forceFullResync || modelIdChanged || prev.length === 0) {
        subscribeToPrices(socket, modelProviderIds, modelId)
      } else {
        if (added.length > 0) addPriceSubscription(socket, added, modelId)
        if (removed.length > 0) removePriceSubscription(socket, removed)
      }
      subscribedIdsRef.current = [...modelProviderIds]
    }

    const handleConnect = (): void => reconcilePriceSubscriptions(true)

    // Server sends diff-only payloads. Merge into accumulated cache so that
    // a tick containing only changed securities does not blank the rest.
    const handlePricesUpdated = (payload: PricesUpdatedPayload): void => {
      setPrices(prev => ({ ...prev, ...payload }))
      setLastUpdatedAt(Date.now())
      setUpdatedModelProviderIds(prev => union(prev, Object.keys(payload ?? {})))
    }

    socket.on("connect", handleConnect)
    socket.on(PRICES_UPDATED_EVENT, handlePricesUpdated)

    // Only sync immediately if already connected. If not yet connected,
    // handleConnect will perform a full resync when the connection is established,
    // avoiding a double subscription event that would hit the server rate limit.
    if (socket.connected) {
      reconcilePriceSubscriptions(false)
    }

    return () => {
      socket.off("connect", handleConnect)
      socket.off(PRICES_UPDATED_EVENT, handlePricesUpdated)
    }
  }, [enabled, accountId, modelId, modelProviderIds, registerSocketConsumer])

  return { prices, lastUpdatedAt, updatedModelProviderIds }
}
